import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";

const EMPLOYMENT_OPTIONS = [
  "Employed – Full-time", "Employed – Part-time", "Self-employed / Business owner",
  "Unemployed (looking for work)", "Unemployed (not looking)", "Student", "Retired",
  "Unable to work / Disabled", "Homemaker / Stay-at-home",
  "Contractor / Freelance / Gig worker", "Temporary / Seasonal", "Other",
];

const EMPTY = {
  first_name: "", last_name: "", email: "", phone: "", street: "", city: "", state: "", zip: "", country: "",
  preferred_contact_method: "", availability: "",
  case_type: "", incident_date: "", incident_time: "", incident_location: "", incident_description: "", desired_outcome: "",
  prior_legal_action: false, prior_case_number: "", prior_status: "", prior_description: "", prior_outcome: "",
  contacted_other_firms: false, contacted_other_firms_details: "",
  employment_status: "", employment_status_other: "", responsible_for_fees: false,
  opposing_parties: [], witnesses: [], medical_providers: [],
  lead_source: "", notes: "",
};

const inp = "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]";
const lbl = "block text-xs font-bold uppercase tracking-wide text-gray-500 mb-1";

function Section({ title, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-sm p-5 shadow-sm">
      <h3 className="font-heading text-base font-bold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function IntakeForm({ initial, caseTypes = [], contactMethods = [], onSubmit, submitting, submitLabel = "Submit" }) {
  const [v, setV] = useState({ ...EMPTY, ...(initial || {}) });
  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const setRow = (key, i, field, val) => setV((p) => ({ ...p, [key]: p[key].map((r, idx) => (idx === i ? { ...r, [field]: val } : r)) }));
  const newKey = () => (window.crypto?.randomUUID?.() || `k-${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const addRow = (key, blank) => setV((p) => ({ ...p, [key]: [...p[key], { ...blank, _key: newKey() }] }));
  const delRow = (key, i) => setV((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const submit = (e) => {
    e.preventDefault();
    const strip = (arr) => (arr || []).map(({ _key, ...rest }) => rest);
    onSubmit({
      ...v,
      opposing_parties: strip(v.opposing_parties),
      witnesses: strip(v.witnesses),
      medical_providers: strip(v.medical_providers),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="intake-form">
      <Section title="Contact information">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>First name *</label><input data-testid="intake-first-name" className={inp} value={v.first_name} onChange={(e) => set("first_name", e.target.value)} required /></div>
          <div><label className={lbl}>Last name *</label><input data-testid="intake-last-name" className={inp} value={v.last_name} onChange={(e) => set("last_name", e.target.value)} required /></div>
          <div><label className={lbl}>Email</label><input type="email" data-testid="intake-email" className={inp} value={v.email} onChange={(e) => set("email", e.target.value)} /></div>
          <div><label className={lbl}>Phone</label><input data-testid="intake-phone" className={inp} value={v.phone} onChange={(e) => set("phone", e.target.value)} placeholder="555-123-4567" /></div>
          <div className="sm:col-span-2"><label className={lbl}>Street address</label><input className={inp} value={v.street} onChange={(e) => set("street", e.target.value)} /></div>
          <div><label className={lbl}>City</label><input className={inp} value={v.city} onChange={(e) => set("city", e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>State</label><input className={inp} value={v.state} onChange={(e) => set("state", e.target.value)} /></div>
            <div><label className={lbl}>ZIP</label><input className={inp} value={v.zip} onChange={(e) => set("zip", e.target.value)} /></div>
          </div>
          <div><label className={lbl}>Preferred contact</label>
            <select className={inp} value={v.preferred_contact_method} onChange={(e) => set("preferred_contact_method", e.target.value)}>
              <option value="">—</option>{contactMethods.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Best availability</label><input className={inp} value={v.availability} onChange={(e) => set("availability", e.target.value)} placeholder="e.g. weekday mornings" /></div>
        </div>
      </Section>

      <Section title="Case details">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Case type</label>
            <select data-testid="intake-case-type" className={inp} value={v.case_type} onChange={(e) => set("case_type", e.target.value)}>
              <option value="">Select case type…</option>{caseTypes.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={lbl}>Incident date</label><input type="date" className={inp} value={v.incident_date} onChange={(e) => set("incident_date", e.target.value)} /></div>
            <div><label className={lbl}>Time</label><input type="time" className={inp} value={v.incident_time} onChange={(e) => set("incident_time", e.target.value)} /></div>
          </div>
          <div className="sm:col-span-2"><label className={lbl}>Location of incident</label><input className={inp} value={v.incident_location} onChange={(e) => set("incident_location", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Description of incident</label><textarea rows={3} className={inp} value={v.incident_description} onChange={(e) => set("incident_description", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className={lbl}>Desired outcome</label><input className={inp} value={v.desired_outcome} onChange={(e) => set("desired_outcome", e.target.value)} /></div>
        </div>
      </Section>

      <Section title="Case history">
        <label className="flex items-center gap-2 text-sm text-gray-700 mb-2"><input type="checkbox" className="accent-[#002FA7]" checked={v.prior_legal_action} onChange={(e) => set("prior_legal_action", e.target.checked)} /> Prior legal action on this matter?</label>
        {v.prior_legal_action && (
          <div className="grid sm:grid-cols-2 gap-3 mb-3 pl-6">
            <div><label className={lbl}>Case number</label><input className={inp} value={v.prior_case_number} onChange={(e) => set("prior_case_number", e.target.value)} /></div>
            <div><label className={lbl}>Status</label><input className={inp} value={v.prior_status} onChange={(e) => set("prior_status", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={lbl}>Description</label><input className={inp} value={v.prior_description} onChange={(e) => set("prior_description", e.target.value)} /></div>
            <div className="sm:col-span-2"><label className={lbl}>Outcome</label><input className={inp} value={v.prior_outcome} onChange={(e) => set("prior_outcome", e.target.value)} /></div>
          </div>
        )}
        <label className="flex items-center gap-2 text-sm text-gray-700 mb-2"><input type="checkbox" className="accent-[#002FA7]" checked={v.contacted_other_firms} onChange={(e) => set("contacted_other_firms", e.target.checked)} /> Contacted other law firms about this matter?</label>
        {v.contacted_other_firms && <input className={inp + " mb-3"} placeholder="Which firms / details" value={v.contacted_other_firms_details} onChange={(e) => set("contacted_other_firms_details", e.target.value)} />}
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className={lbl}>Employment status</label>
            <select data-testid="intake-employment-status" className={inp} value={v.employment_status} onChange={(e) => set("employment_status", e.target.value)}>
              <option value="">Select…</option>
              {EMPLOYMENT_OPTIONS.map((o) => <option key={o} value={o}>{o === "Other" ? "Other (please specify)" : o}</option>)}
            </select>
            {v.employment_status === "Other" && (
              <input data-testid="intake-employment-other" className={inp + " mt-2"} placeholder="Please specify"
                value={v.employment_status_other} onChange={(e) => set("employment_status_other", e.target.value)} />
            )}
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700 mt-6"><input type="checkbox" className="accent-[#002FA7]" checked={v.responsible_for_fees} onChange={(e) => set("responsible_for_fees", e.target.checked)} /> Responsible for legal fees?</label>
        </div>
      </Section>

      <RepeatSection title="Opposing parties" testid="opposing" rows={v.opposing_parties}
        onAdd={() => addRow("opposing_parties", { name: "", relationship: "", phone: "", email: "", address: "", insurance_company: "", policy_number: "" })}
        onDel={(i) => delRow("opposing_parties", i)}
        fields={[["name", "Name"], ["relationship", "Relationship"], ["phone", "Phone"], ["email", "Email"], ["insurance_company", "Insurance company"], ["policy_number", "Policy #"], ["address", "Address", true]]}
        onChange={(i, f, val) => setRow("opposing_parties", i, f, val)} />

      <RepeatSection title="Witnesses" testid="witness" rows={v.witnesses}
        onAdd={() => addRow("witnesses", { name: "", relationship: "", phone: "", email: "" })}
        onDel={(i) => delRow("witnesses", i)}
        fields={[["name", "Name"], ["relationship", "Relationship"], ["phone", "Phone"], ["email", "Email"]]}
        onChange={(i, f, val) => setRow("witnesses", i, f, val)} />

      <RepeatSection title="Medical providers" testid="medical" rows={v.medical_providers}
        onAdd={() => addRow("medical_providers", { provider_name: "", contact_name: "", address: "", phone: "", fax: "", provider_type: "", first_visit_date: "", diagnosis: "", treatment_status: "" })}
        onDel={(i) => delRow("medical_providers", i)}
        fields={[["provider_name", "Provider name"], ["provider_type", "Type"], ["contact_name", "Contact"], ["phone", "Phone"], ["fax", "Fax"], ["first_visit_date", "First visit", false, "date"], ["diagnosis", "Diagnosis/injury"], ["treatment_status", "Treatment status"], ["address", "Address", true]]}
        onChange={(i, f, val) => setRow("medical_providers", i, f, val)} />

      <Section title="Referral & notes">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Lead source</label><input data-testid="intake-lead-source" className={inp} value={v.lead_source} onChange={(e) => set("lead_source", e.target.value)} placeholder="Website, referral, advertisement…" /></div>
        </div>
        <div className="mt-3"><label className={lbl}>Notes</label><textarea rows={3} className={inp} value={v.notes} onChange={(e) => set("notes", e.target.value)} /></div>
      </Section>

      <div className="flex justify-end">
        <button type="submit" disabled={submitting} data-testid="intake-submit-button"
          className="bg-[#002FA7] hover:bg-[#002480] text-white px-6 py-2.5 rounded-sm font-semibold text-sm disabled:opacity-60">
          {submitting ? "Saving…" : submitLabel}
        </button>
      </div>
    </form>
  );
}

function RepeatSection({ title, testid, rows, fields, onAdd, onDel, onChange }) {
  return (
    <Section title={title}>
      {rows.length === 0 && <p className="text-xs text-gray-400 mb-3">None added.</p>}
      <div className="space-y-3">
        {rows.map((row, i) => (
          <div key={row._key || `${testid}-${i}`} className="border border-gray-100 rounded-sm p-3 bg-gray-50/60 relative" data-testid={`${testid}-row-${i}`}>
            <button type="button" onClick={() => onDel(i)} data-testid={`${testid}-remove-${i}`} className="absolute top-2 right-2 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
            <div className="grid sm:grid-cols-2 gap-2 pr-6">
              {fields.map(([f, label, full, type]) => (
                <div key={f} className={full ? "sm:col-span-2" : ""}>
                  <label className={lbl}>{label}</label>
                  <input type={type || "text"} className={inp} value={row[f] || ""} onChange={(e) => onChange(i, f, e.target.value)} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <button type="button" onClick={onAdd} data-testid={`${testid}-add`} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#002FA7] hover:underline"><Plus className="w-4 h-4" /> Add {title.toLowerCase().replace(/s$/, "")}</button>
    </Section>
  );
}
